import type { DeepRequired, DeepOmit } from "@endgame/utils/types";
import type * as SVGO from "svgo";

type SVGOPlugin =
  | string
  | {
      name: string;
      params?: Record<string, unknown>;
    }
  | SVGO.CustomPlugin;

type SVGOPickedConfig = {
  multipass?: boolean;
  floatPrecision?: number;
  plugins?: SVGOPlugin[];
};

type FigmaFilesVersion = "published" | "saved" | "live";

type FileFormatsTarget =
  | "tw"
  | "ts"
  | "esm"
  | "cjs"
  | "json"
  | "css"
  | "scss"
  | "sass"
  | "less";

type FileFormatsTargets = FileFormatsTarget[];

type ColorFormats = "hex" | "rgba" | "hsla";

type LengthDimensionUnit = "px" | "rem";

interface VariablesNumberFormatter {
  /**
   * Target number variables.
   *
   * Docs: https://zeytal.com/docs/variables-config#numbers-format
   */
  mapping: TokenMatcherArray;
  /**
   * Choose the unit of the targeted number variables.
   *
   * Docs: https://zeytal.com/docs/variables-config#numbers-format
   */
  unit: LengthDimensionUnit;
}
type VariablesNumberFormats = VariablesNumberFormatter[];

type TokenMatcher = string;
type TokenMatcherArray = TokenMatcher[];
type TokenMatcherRecord = Record<TokenMatcher, string>;

export type CLIConfig = {
  /**
   * Zeytal project secret token.
   */
  projectToken?: string;
  /**
   * Figma assets generation config object.
   *
   * Docs: https://zeytal.com/docs/assets-config
   */
  assets?: {
    /**
     * Get the correct version of your Figma file to generate assets from.
     *
     * Docs: https://zeytal.com/docs/assets-config#figma-files-version
     */
    figmaFilesVersion?: FigmaFilesVersion;
    /**
     * Set the number of Figma file pages to fetch at the same time.
     *
     * Docs: https://zeytal.com/docs/assets-config#figma-files-pages-batch-size
     */
    figmaFilesPagesBatchSize?: number;
    /**
     * This is the directory path into which your optimized assets will be written.
     *
     * This path is relative to your root directory.
     *
     * Docs: https://zeytal.com/docs/assets-config#directory-path
     */
    directoryPath?: string;
    /**
     * Figma assets generation optimization config object.
     *
     * Docs: https://zeytal.com/docs/assets-config#svgs-optimization
     */
    optimize?: {
      /**
       * SVG files optimization config.
       *
       * Docs: https://zeytal.com/docs/assets-config#svgs-optimization
       */
      svg?: SVGOPickedConfig;
    };
  };
  /**
   * Figma styles generation config object.
   *
   * Docs: https://zeytal.com/docs/styles-config
   */
  styles?: {
    /**
     * Get the correct version of your Figma file to generate styles from.
     *
     * Docs: https://zeytal.com/docs/styles-config#figma-files-version
     */
    figmaFilesVersion?: FigmaFilesVersion;
    /**
     * Set the number of Figma file pages to fetch at the same time.
     *
     * Docs: https://zeytal.com/docs/styles-config#figma-files-pages-batch-size
     */
    figmaFilesPagesBatchSize?: number;
    /**
     * This is the directory path into which your transformed styles will be written.
     *
     * This path is relative to your root directory.
     *
     * Docs: https://zeytal.com/docs/styles-config#directory-path
     */
    directoryPath?: string;
    /**
     * Figma styles file formats config object.
     *
     * Docs: https://zeytal.com/docs/styles-config#output-formats
     */
    formats?: {
      /**
       * This setting controls the format of your generated styles files.
       *
       * Docs: https://zeytal.com/docs/styles-config#output-formats
       */
      targets?: FileFormatsTargets;
      /**
       * Color styles config object.
       *
       * Docs: https://zeytal.com/docs/styles-config#colors-format
       */
      colors?: {
        /**
         * This setting controls the color format in which your color styles are generated.
         *
         * Docs: https://zeytal.com/docs/styles-config#colors-format
         */
        format?: ColorFormats;
      };
      /**
       * Font styles config object.
       *
       * Docs: https://zeytal.com/docs/styles-config#use-postscript-font-names
       */
      fonts?: {
        /**
         * Choose whether or not to use the `fontPostScriptName` value as `fontName`.
         *
         * Docs: https://zeytal.com/docs/styles-config#use-postscript-font-names
         */
        usePostscriptFontNames?: boolean;
        /**
         * Choose the unit of your generated font styles.
         *
         * Docs: https://zeytal.com/docs/styles-config#fonts-unit
         */
        unit?: LengthDimensionUnit;
        /**
         * Choose whether or not to use a line-height value relative to the font size.
         *
         * Docs: https://zeytal.com/docs/styles-config#fonts-relative-line-height
         */
        relativeLineHeight?: boolean;
      };
      /**
       * Effect styles config object.
       *
       * Docs: https://zeytal.com/docs/styles-config#combine-effects
       */
      effects?: {
        /**
         * Choose whether or not to combine effects that belong to the same CSS property.
         *
         * Docs: https://zeytal.com/docs/styles-config#combine-effects
         */
        combine?: boolean;
      };
      /**
       * Grid styles config object.
       *
       * Docs: https://zeytal.com/docs/styles-config#extract-grid-colors
       */
      grids?: {
        /**
         * Choose whether or not to extract the colors of the grid styles.
         *
         * Docs: https://zeytal.com/docs/styles-config#extract-grid-colors
         */
        extractColors?: boolean;
      };
    };
  };
  /**
   * Figma variables generation config object.
   *
   * Docs: https://zeytal.com/docs/variables-config
   */
  variables?: {
    /**
     * This is the directory path into which your transformed variables will be written.
     *
     * This path is relative to your root directory.
     *
     * Docs: https://zeytal.com/docs/variables-config#directory-path
     */
    directoryPath?: string;
    /**
     * Choose whether or not to exclude Figma variable collections from the variables generation process.
     *
     * Docs: https://zeytal.com/docs/variables-config#collections-ignore-prefix
     */
    collectionsIgnorePrefix?: string;
    /**
     * Figma variables file formats config object.
     *
     * Docs: https://zeytal.com/docs/variables-config#output-formats
     */
    formats?: {
      /**
       * This setting controls the format of your generated variables files.
       *
       * Docs: https://zeytal.com/docs/variables-config#output-formats
       */
      targets?: FileFormatsTargets;
      /**
       * Color variables config object.
       *
       * Docs: https://zeytal.com/docs/variables-config#colors-format
       */
      colors?: {
        /**
         * This setting controls the color format in which your color variables are generated.
         *
         * Docs: https://zeytal.com/docs/variables-config#colors-format
         */
        format?: ColorFormats;
      };
      /**
       * Number variables config object.
       *
       * Docs: https://zeytal.com/docs/variables-config#numbers-format
       */
      numbers?: {
        /**
         * This setting specifies the number format in which some of your number variables will be generated.
         *
         * Docs: https://zeytal.com/docs/variables-config#numbers-format
         */
        format?: VariablesNumberFormats;
      };
    };
  };
  /**
   * Figma tailwind config generation object.
   *
   * Docs: https://zeytal.com/docs/tailwind-config
   */
  tailwind?: {
    /**
     * Add a prefix to your generated TailwindCSS theme variables.
     *
     * Docs: https://zeytal.com/docs/tailwind-config#tokens-prefix
     */
    tokensPrefix?: string;
    /**
     * Generate scoped CSS variable declarations to automatically handle Figma variable modes.
     *
     * Docs: https://zeytal.com/docs/tailwind-config#global-modes
     */
    globalModes?: TokenMatcherArray[];
    /**
     * Exclude matching Figma styles and Figma variables from your generated TailwindCSS config files.
     *
     * Docs: https://zeytal.com/docs/tailwind-config#global-ignore-token-names
     */
    globalIgnoreTokenNames?: TokenMatcherArray;
    /**
     * Remove matching parts of your Figma style and Figma variable names.
     *
     * Docs: https://zeytal.com/docs/tailwind-config#global-strip-token-names
     */
    globalStripTokenNames?: TokenMatcherArray;
    /**
     * Rewrite matching parts of your Figma style and Figma variable names.
     *
     * Docs: https://zeytal.com/docs/tailwind-config#global-rewrite-token-names
     */
    globalRewriteTokenNames?: TokenMatcherRecord;
    /**
     * TailwindCSS theme namespaces config object.
     *
     * Docs: https://zeytal.com/docs/tailwind-config#namespaces
     */
    namespaces?: {
      /**
       * TailwindCSS font family namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      fontFamily?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS font family namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS font family namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS font family namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS font size namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      fontSize?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS font size namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS font size namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS font size namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS font weight namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      fontWeight?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS font weight namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS font weight namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS font weight namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS letter spacing namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      letterSpacing?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS letter spacing namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS letter spacing namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS letter spacing namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS line height namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      lineHeight?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS line height namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS line height namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS line height namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS color namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      color?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS color namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS color namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS color namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS spacing namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      spacing?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS spacing namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS spacing namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS spacing namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS breakpoint namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      breakpoint?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS breakpoint namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS breakpoint namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS breakpoint namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS container namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      container?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS container namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS container namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS container namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS radius namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      radius?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS radius namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS radius namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS radius namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS shadow namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      shadow?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS shadow namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS shadow namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS shadow namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS inset shadow namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      insetShadow?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS inset shadow namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS inset shadow namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS inset shadow namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS drop shadow namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      dropShadow?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS drop shadow namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS drop shadow namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS drop shadow namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS blur namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      blur?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS blur namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS blur namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS blur namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS perspective namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      perspective?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS perspective namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS perspective namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS perspective namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS aspect namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      aspect?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS aspect namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS aspect namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS aspect namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS ease namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      ease?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS ease namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS ease namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS ease namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
      /**
       * TailwindCSS animate namespace config object.
       *
       * Docs: https://zeytal.com/docs/tailwind-config#namespaces
       * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
       */
      animate?: {
        /**
         * Map your Figma styles and Figma variables to your TailwindCSS animate namespace by name-matching them.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#tokens-mapping
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        tokensMapping?: TokenMatcherArray;
        /**
         * Remove matching parts of your TailwindCSS animate namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#strip-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        stripTokenNames?: TokenMatcherArray;
        /**
         * Rewrite matching parts of your TailwindCSS animate namespace theme variables.
         *
         * Docs: https://zeytal.com/docs/tailwind-config#rewrite-token-names
         * Tailwind docs: https://tailwindcss.com/docs/theme#theme-variable-namespaces
         */
        rewriteTokenNames?: TokenMatcherRecord;
      };
    };
  };
};

export type CLIPureConfig = DeepOmit<
  DeepRequired<CLIConfig>,
  "assets.optimize.svg"
> & {
  assets: {
    optimize: {
      svg: SVGOPickedConfig;
    };
  };
};

export type ZeytalCLIConfigSchema = {
  /**
   * Ignored. Can be set to get completions, validations and documentation in some editors.
   *
   * Docs: https://zeytal.com/docs/cli-config-files#schema-autocomplete
   */
  $schema: "node_modules/zeytal/schema/zeytal.json";
} & Omit<CLIConfig, "projectToken">;

export const zeytalDefaultConfig: CLIPureConfig = {
  projectToken: "",
  assets: {
    figmaFilesVersion: "live",
    figmaFilesPagesBatchSize: 5,
    directoryPath: ".zeytal/assets",
    optimize: {
      svg: {},
    },
  },
  styles: {
    figmaFilesVersion: "live",
    figmaFilesPagesBatchSize: 5,
    directoryPath: ".zeytal/styles",
    formats: {
      targets: [],
      colors: {
        format: "hex",
      },
      fonts: {
        usePostscriptFontNames: false,
        unit: "px",
        relativeLineHeight: false,
      },
      effects: {
        combine: true,
      },
      grids: {
        extractColors: false,
      },
    },
  },
  variables: {
    directoryPath: ".zeytal/variables",
    collectionsIgnorePrefix: "",
    formats: {
      targets: [],
      colors: {
        format: "hex",
      },
      numbers: {
        format: [],
      },
    },
  },
  tailwind: {
    tokensPrefix: "",
    globalModes: [],
    globalIgnoreTokenNames: [],
    globalStripTokenNames: [],
    globalRewriteTokenNames: {},
    namespaces: {
      fontFamily: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      fontSize: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      fontWeight: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      letterSpacing: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      lineHeight: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      color: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      spacing: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      breakpoint: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      container: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      radius: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      shadow: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      insetShadow: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      dropShadow: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      blur: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      perspective: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      aspect: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      ease: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
      animate: {
        tokensMapping: [],
        stripTokenNames: [],
        rewriteTokenNames: {},
      },
    },
  },
};
